<script setup lang="ts">
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue'

const { t } = useI18n()
const { currentUser } = storeToRefs(useCurrentUserStore())
const { selectedOrganization, userOrganizations } = storeToRefs(useCurrentOrganizationStore())
const { currentOrganizationRoles } = useCurrentOrganizationRoles()

// Get the primary role for display, just pick the first one to keep things simple
const primaryRole = computed(() => {
  if (currentOrganizationRoles.value.length === 0) return null
  return currentOrganizationRoles.value?.[0]
})

const hasMultipleRoles = computed(() => userOrganizations.value.length > 1)

</script>

<template>
  <div
    v-if="currentUser"
    class="group inline-flex items-center justify-center text-right"
  >
    <Menu
      v-slot="{ close }"
      as="div"
      class="relative size-9 text-left"
    >
      <MenuButton as="div">
        <button
          type="button"
          class=" inline-flex size-9 items-center justify-center rounded-full transition-all duration-300 "
        >
          <div
            class="relative inline-flex size-9 items-center justify-center rounded-full"
          >
            <UserAvatar :key="currentUser.id" :user="currentUser" />
          </div>
        </button>
      </MenuButton>

      <Transition
        enter-active-class="transition duration-100 ease-out"
        enter-from-class="transform scale-95 opacity-0"
        enter-to-class="transform scale-100 opacity-100"
        leave-active-class="transition duration-75 ease-in"
        leave-from-class="transform scale-100 opacity-100"
        leave-to-class="transform scale-95 opacity-0"
      >
        <MenuItems
          class="divide-muted-100 border-muted-200 dark:divide-muted-700 dark:border-muted-700 dark:bg-muted-800 absolute end-0 z-50 mt-2 w-64 origin-top-right divide-y rounded-md border bg-white shadow-lg"
        >
          <!-- Current Role & Organization -->
          <MenuItem v-if="hasMultipleRoles" v-slot="{ active }">
            <NuxtLink
              to="/account/roles"
              class="block border-2 px-6 py-4 transition-colors duration-200"
              :class="[
                active ? 'bg-muted-100 dark:bg-muted-900/60 border-primary-500 dark:border-primary-400' : 'bg-muted-50 dark:bg-muted-950 border-transparent',
              ]"
            >
              <div class="flex items-center justify-between">
                <div class="flex min-w-0 flex-1 items-center gap-3">
                  <OrganizationAvatar
                    v-if="selectedOrganization"
                    size="xs"
                    :organization="selectedOrganization"
                    :show-tooltip="false"
                  />
                  <Icon
                    v-else
                    name="ph:buildings"
                    class="text-muted-400 size-6 shrink-0"
                  />

                  <div class="min-w-0 flex-1">
                    <BaseText
                      size="sm"
                      class="text-muted-800 dark:text-muted-200 truncate font-medium"
                    >
                      {{ selectedOrganization?.name }}
                    </BaseText>
                    <BaseText
                      v-if="primaryRole"
                      size="xs"
                      class="text-muted-500 dark:text-muted-400 truncate"
                    >
                      {{ t(`user.attributes.roles.${primaryRole.name}`) }}
                    </BaseText>
                  </div>
                </div>

                <Icon
                  name="ph:caret-right"
                  class="text-muted-400 size-4 shrink-0"
                />
              </div>
            </NuxtLink>
          </MenuItem>
          <div v-else class="bg-muted-50 dark:bg-muted-950 block px-6 py-4 transition-colors duration-200">
            <div class="flex min-w-0 flex-1 items-center gap-3">
              <OrganizationAvatar
                v-if="selectedOrganization"
                size="xs"
                :organization="selectedOrganization"
                :show-tooltip="false"
              />
              <Icon
                v-else
                name="ph:buildings"
                class="text-muted-400 size-6 shrink-0"
              />

              <div class="min-w-0 flex-1">
                <BaseText
                  size="sm"
                  class="text-muted-800 dark:text-muted-200 truncate font-medium"
                >
                  {{ selectedOrganization?.name }}
                </BaseText>
                <BaseText
                  v-if="primaryRole"
                  size="xs"
                  class="text-muted-500 dark:text-muted-400 truncate"
                >
                  {{ t(`user.attributes.roles.${primaryRole.name}`) }}
                </BaseText>
              </div>
            </div>
          </div>

          <!-- User info - avatar and name -->
          <div class="p-6 text-center">
            <UserAvatar :user="currentUser" size="xl" />

            <div v-if="currentUser" class="mt-3">
              <h6
                class="font-heading text-muted-800 text-sm font-medium dark:text-white"
              >
                Welkom
                {{ currentUser.firstName }}!
              </h6>
              <p class="text-muted-400 mb-4 font-sans text-xs">
                {{ currentUser.email }}
              </p>
              <MenuItem
                v-slot="{ active }"
              >
                <BaseButton
                  :color="active ? 'success' : 'primary'"
                  to="/profile-edit"
                  shape="curved"
                  class="w-full"
                  @click="close"
                >
                  <Icon name="ph:user-circle-duotone" class="mr-1" />
                  {{ t('manage') }}
                </BaseButton>
              </MenuItem>
            </div>
          </div>

          <!-- Logout button -->
          <div class="p-6">
            <MenuItem v-slot="{ active }">
              <BaseButton
                :color="active ? 'success' : 'primary'"
                to="/uitloggen"
                shape="curved"
                class="w-full"
                @click="close"
              >
                <Icon name="ph:sign-out-duotone" class="mr-1" />
                {{ t('logout') }}
              </BaseButton>
            </MenuItem>
          </div>
        </MenuItems>
      </Transition>
    </Menu>
  </div>
</template>

<i18n lang="json">
{
  "en": {
    "logout": "Logout",
    "manage": "Manage account",
    "selectOrganization": "Select organization",
    "switchRole": "Wijzig je rol",
    "viewOrganization": "View organization details"
  },
  "nl": {
    "logout": "Uitloggen",
    "manage": "Beheer account",
    "selectOrganization": "Selecteer organisatie",
    "switchRole": "Wijzig je rol",
    "viewOrganization": "Bekijk organisatie details"
  }
}
</i18n>
